/**
 * morta.chart: helpers for what the kit's chart CSS can't do, put in every page by the bootstrap (runtime.ts).
 * Bars rounded at one end with the theme's radius, the tooltip card, the legend, and series colours. Plain
 * JavaScript run inside the page, so it's a string; it defines `chart`, which becomes morta.chart.
 */
export const CHART_SCRIPT = `
const ROUNDED = { top: [1, 1, 0, 0], bottom: [0, 0, 1, 1], right: [0, 1, 1, 0], left: [1, 0, 0, 1], all: [1, 1, 1, 1], none: [0, 0, 0, 0] };
const finite = (value) => (Number.isFinite(value) ? value : 0);
// Bars' corners follow the theme's control radius unless a chart asks for another.
const themeRadius = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--morta-radius-sm')) || 4;
const barPath = (x, y, width, height, { radius = themeRadius(), round = 'top' } = {}) => {
  // Negative sizes (a bar below zero) are drawn from the other corner.
  let left = finite(x), top = finite(y), w = finite(width), h = finite(height);
  if (w < 0) { left += w; w = -w; }
  if (h < 0) { top += h; h = -h; }
  const corners = ROUNDED[round] || ROUNDED.top;
  const sideways = round === 'left' || round === 'right';
  const limit = round === 'all' ? Math.min(w, h) / 2 : sideways ? Math.min(h / 2, w) : Math.min(w / 2, h);
  const r = Math.max(0, Math.min(finite(radius), limit));
  const [tl, tr, br, bl] = corners.map((on) => on * r);
  const arc = (size, toX, toY) => (size ? ' A' + size + ' ' + size + ' 0 0 1 ' + toX + ' ' + toY : ' L' + toX + ' ' + toY);
  return 'M' + (left + tl) + ' ' + top + ' H' + (left + w - tr) + arc(tr, left + w, top + tr) +
    ' V' + (top + h - br) + arc(br, left + w - br, top + h) + ' H' + (left + bl) + arc(bl, left, top + h - bl) +
    ' V' + (top + tl) + arc(tl, left + tl, top) + ' Z';
};
// A series' key in a tooltip or legend: a square, or a short stroke for a line.
const keySwatch = (item) => {
  const swatch = document.createElement('span');
  swatch.className = 'm-chart-swatch';
  if (item.colour) swatch.style.setProperty('--m-swatch', String(item.colour));
  if (item.key === 'line') swatch.dataset.key = 'line';
  return swatch;
};
const chartTooltip = () => {
  let element = null;
  return {
    // items: [{ name, value, colour, key }]; the value as the page formats it, key 'line' for a line's series.
    show(event, { title = '', items = [] } = {}) {
      if (!element) {
        element = document.createElement('div');
        element.className = 'm-chart-tooltip';
        element.setAttribute('role', 'status');
        document.body.append(element);
      }
      const rows = [];
      if (title !== '') {
        const heading = document.createElement('div');
        heading.className = 'm-chart-tooltip-title';
        heading.textContent = String(title);
        rows.push(heading);
      }
      for (const item of items) {
        const row = document.createElement('div');
        row.className = 'm-chart-tooltip-row';
        const swatch = keySwatch(item);
        const name = document.createElement('span');
        name.className = 'm-chart-tooltip-name';
        name.textContent = String(item.name ?? '');
        const value = document.createElement('span');
        value.className = 'm-chart-tooltip-value';
        value.textContent = String(item.value ?? '');
        row.append(swatch, name, value);
        rows.push(row);
      }
      element.replaceChildren(...rows);
      element.hidden = false;
      // Beside the pointer, flipped to stay on screen.
      const box = element.getBoundingClientRect();
      const x = event.clientX + 12 + box.width > window.innerWidth ? event.clientX - 12 - box.width : event.clientX + 12;
      const y = event.clientY + 12 + box.height > window.innerHeight ? event.clientY - 12 - box.height : event.clientY + 12;
      element.style.left = Math.max(4, x) + 'px';
      element.style.top = Math.max(4, y) + 'px';
    },
    hide() { if (element) element.hidden = true; }
  };
};
const chartLegend = (container, items = []) => {
  if (!(container instanceof Element)) throw new TypeError('morta.chart.legend needs an element');
  container.classList.add('m-chart-legend');
  container.replaceChildren(...items.map((item) => {
    const entry = document.createElement('span');
    entry.className = 'm-chart-legend-item';
    const swatch = keySwatch(item);
    entry.append(swatch, document.createTextNode(String(item.name ?? '')));
    return entry;
  }));
};
const chart = Object.freeze({
  // A series' colour in order, the hub's first, as a CSS value for .style('fill', …).
  colour: (index) => 'var(--morta-series-' + ((Math.abs(Math.trunc(finite(index))) % 8) + 1) + ')',
  bar: barPath,
  tooltip: chartTooltip,
  legend: chartLegend
});
`
